import React, { useState, useEffect } from 'react';
import { FaAd, FaPlus, FaEdit, FaTrash, FaToggleOn, FaToggleOff, FaEye, FaImage, FaVideo, FaFileAlt } from 'react-icons/fa';
import { adminAPI, adsAPI } from '../../services/api';
import { toast } from 'react-toastify';

const AdminAds = () => {
  const [ads, setAds] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null);
  const [adTypes, setAdTypes] = useState([]);
  const [adLocations, setAdLocations] = useState([]);

  // Filters
  const [filters, setFilters] = useState({
    is_active: 'all',
    location: 'all',
    page: 1,
    limit: 20
  });

  useEffect(() => {
    fetchAds();
    fetchStats();
    fetchAdTypes();
    fetchAdLocations();
  }, [filters]);

  const fetchAds = async () => {
    try {
      const response = await adminAPI.getAds(filters);
      if (response.success) {
        setAds(response.ads);
        setPagination(response.pagination);
      }
    } catch (error) {
      console.error('Error fetching ads:', error);
      toast.error('Failed to load ads');
    }
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAdsStats();
      if (response.success) {
        setStats(response);
        setError(null);
      }
    } catch (error) {
      console.error('Error fetching ads stats:', error);
      setError('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdTypes = async () => {
    try {
      const response = await adsAPI.getAdTypes();
      if (response.success) {
        setAdTypes(response.types);
      }
    } catch (error) {
      console.error('Error fetching ad types:', error);
    }
  };

  const fetchAdLocations = async () => {
    try {
      const response = await adsAPI.getAdLocations();
      if (response.success) {
        setAdLocations(response.locations);
      }
    } catch (error) {
      console.error('Error fetching ad locations:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key === 'page' ? value : 1
    }));
  };

  const handleCreateAd = async (adData) => {
    try {
      const response = await adminAPI.createAd(adData);
      if (response.success) {
        toast.success('Ad created successfully');
        setShowCreateModal(false);
        fetchAds();
        fetchStats();
      }
    } catch (error) {
      console.error('Error creating ad:', error);
      toast.error('Failed to create ad');
    }
  };

  const handleUpdateAd = async (adData) => {
    try {
      const response = await axios.put(`/api/admin/ads/${selectedAd.id}`, adData);
      if (response.data.success) {
        toast.success('Ad updated successfully');
        setShowEditModal(false);
        fetchAds();
      }
    } catch (error) {
      console.error('Error updating ad:', error);
      toast.error('Failed to update ad');
    }
  };

  const handleDeleteAd = async (adId) => {
    if (!window.confirm('Are you sure you want to delete this ad?')) return;

    try {
      const response = await axios.delete(`/api/admin/ads/${adId}`);
      if (response.data.success) {
        toast.success('Ad deleted successfully');
        fetchAds();
        fetchStats();
      }
    } catch (error) {
      console.error('Error deleting ad:', error);
      toast.error('Failed to delete ad');
    }
  };

  const handleToggleAdStatus = async (adId) => {
    try {
      const response = await axios.patch(`/api/admin/ads/${adId}/toggle`);
      if (response.data.success) {
        toast.success(`Ad ${response.data.newStatus ? 'activated' : 'deactivated'} successfully`);
        fetchAds();
        fetchStats();
      }
    } catch (error) {
      console.error('Error toggling ad status:', error);
      toast.error('Failed to toggle ad status');
    }
  };

  const getAdTypeIcon = (type) => {
    switch (type) {
      case 'photo':
        return <FaImage className="w-4 h-4" />;
      case 'video':
        return <FaVideo className="w-4 h-4" />;
      case 'gif':
        return <FaFileAlt className="w-4 h-4" />;
      default:
        return <FaAd className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const AdModal = ({ ad, onClose, onSave, isEdit = false }) => {
    const [formData, setFormData] = useState({
      ads_type_id: ad?.ads_type_id || '',
      title: ad?.title || '',
      description: ad?.description || '',
      media: ad?.media || '',
      link: ad?.link || '',
      ads_location_id: ad?.ads_location_id || ''
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      onSave(formData);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-md w-full mx-4">
          <div className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                {isEdit ? 'Edit Ad' : 'Create New Ad'}
              </h2>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <FaPlus className="w-5 h-5 transform rotate-45" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Ad Type</label>
                <select
                  value={formData.ads_type_id}
                  onChange={(e) => setFormData({ ...formData, ads_type_id: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                >
                  <option value="">Select type</option>
                  {adTypes.map((type) => (
                    <option key={type.id} value={type.id}>{type.type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Location</label>
                <select
                  value={formData.ads_location_id}
                  onChange={(e) => setFormData({ ...formData, ads_location_id: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                >
                  <option value="">Select location</option>
                  {adLocations.map((location) => (
                    <option key={location.id} value={location.id}>{location.location.replace('_', ' ')}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Media URL</label>
                <input
                  type="url"
                  value={formData.media}
                  onChange={(e) => setFormData({ ...formData, media: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Link URL</label>
                <input
                  type="url"
                  value={formData.link}
                  onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="https://example.com"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-pink-600 text-white py-2 px-4 rounded-md hover:bg-pink-700"
                >
                  {isEdit ? 'Update Ad' : 'Create Ad'}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 lg:space-y-8 xl:space-y-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-gray-900">Ads Management</h1>
          <p className="text-gray-600 text-sm sm:text-base">Create and manage advertisements</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-pink-600 text-white px-3 sm:px-4 py-2 rounded-md hover:bg-pink-700 flex items-center self-start sm:self-center"
        >
          <FaPlus className="w-4 h-4 mr-2" />
          Create Ad
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 lg:p-8 xl:p-10">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100">
                <FaAd className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Ads</p>
                <p className="text-2xl font-bold text-gray-900">{stats.overview.totalAds}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 lg:p-8 xl:p-10">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100">
                <FaToggleOn className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Ads</p>
                <p className="text-2xl font-bold text-gray-900">{stats.overview.activeAds}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 lg:p-8 xl:p-10">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-red-100">
                <FaToggleOff className="w-6 h-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Inactive Ads</p>
                <p className="text-2xl font-bold text-gray-900">{stats.overview.inactiveAds}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 lg:p-8 xl:p-10">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100">
                <FaEye className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Locations</p>
                <p className="text-2xl font-bold text-gray-900">{stats.byLocation.length}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Charts */}
      {stats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Ads by Location */}
          <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 lg:p-8 xl:p-10">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Ads by Location</h2>
            <div className="space-y-3">
              {stats.byLocation.map((location) => (
                <div key={location.location} className="flex items-center justify-between">
                  <span className="text-sm font-medium">{location.location.replace('_', ' ')}</span>
                  <div className="flex items-center">
                    <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                      <div
                        className="bg-pink-600 h-2 rounded-full"
                        style={{ width: `${(location.count / stats.overview.totalAds) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-600 w-8">{location.count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Ads by Type */}
          <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 lg:p-8 xl:p-10">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Ads by Type</h2>
            <div className="space-y-3">
              {stats.byType.map((type) => (
                <div key={type.type} className="flex items-center justify-between">
                  <span className="text-sm font-medium capitalize">{type.type}</span>
                  <div className="flex items-center">
                    <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${(type.count / stats.overview.totalAds) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-600 w-8">{type.count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.is_active}
              onChange={(e) => handleFilterChange('is_active', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="all">All Status</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <select
              value={filters.location}
              onChange={(e) => handleFilterChange('location', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="all">All Locations</option>
              {adLocations.map((location) => (
                <option key={location.id} value={location.location}>
                  {location.location.replace('_', ' ')}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                fetchAds();
                fetchStats();
              }}
              className="w-full bg-pink-600 text-white py-2 px-4 rounded-md hover:bg-pink-700"
            >
              <FaAd className="inline w-4 h-4 mr-2" />
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* Ads Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ad</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {ads.map((ad) => (
                <tr key={ad.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="text-sm font-medium text-gray-900">{ad.title}</div>
                      {ad.description && (
                        <div className="text-sm text-gray-500 truncate max-w-xs">{ad.description}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {getAdTypeIcon(ad.media_type)}
                      <span className="ml-2 text-sm text-gray-900 capitalize">{ad.media_type}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{ad.location.replace('_', ' ')}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {ad.is_active ? (
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(ad.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => {
                        setSelectedAd(ad);
                        setShowEditModal(true);
                      }}
                      className="text-yellow-600 hover:text-yellow-900"
                      title="Edit Ad"
                    >
                      <FaEdit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleToggleAdStatus(ad.id)}
                      className={ad.is_active ? "text-red-600 hover:text-red-900" : "text-green-600 hover:text-green-900"}
                      title={ad.is_active ? "Deactivate" : "Activate"}
                    >
                      {ad.is_active ? <FaToggleOff className="w-4 h-4" /> : <FaToggleOn className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => handleDeleteAd(ad.id)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete Ad"
                    >
                      <FaTrash className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleFilterChange('page', pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaPlus className="w-4 h-4 transform rotate-180" />
                </button>

                <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                  Page {pagination.page} of {pagination.totalPages}
                </span>

                <button
                  onClick={() => handleFilterChange('page', pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaPlus className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <AdModal
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateAd}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedAd && (
        <AdModal
          ad={selectedAd}
          onClose={() => setShowEditModal(false)}
          onSave={handleUpdateAd}
          isEdit={true}
        />
      )}
    </div>
  );
};

export default AdminAds;